/* eslint-disable no-underscore-dangle */
import { ApiResponseError } from '@/classes/Errors/ApiResponseError';
import { HttpStatus } from '@/constants';

export class ApiHelper {
  private _apiUrl: string;

  private _apiConfig: RequestInit;

  public allowApiKeyAuthFallback: boolean;

  /**
   * @param {boolean} allowApiKeyAuthFallback Whether these requests should allow falling back to
   * the V1 API Key if the V2 API/OAuth Token is missing
   */
  constructor(baseUrl: string, baseConfig: RequestInit = {}, allowApiKeyAuthFallback = false) {
    // Make sure the base URL we're going to use ends in a /
    this._apiUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    this._apiConfig = baseConfig;
    this.allowApiKeyAuthFallback = allowApiKeyAuthFallback;
  }

  delete(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    return this._callApiWithMethod('DELETE', endpoint, options);
  }

  get(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    return this._callApiWithMethod('GET', endpoint, options);
  }

  post(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    return this._callApiWithMethod('POST', endpoint, options);
  }

  put(endpoint: string, options: RequestInit = {}): Promise<unknown> {
    return this._callApiWithMethod('PUT', endpoint, options);
  }

  // necessary to post files with Form Data Api
  unsetContentType() {
    if (this._apiConfig.headers instanceof Headers) {
      this._apiConfig.headers.delete('Content-Type');
    }
    return this;
  }

  resetContentType() {
    if (this._apiConfig.headers instanceof Headers) {
      this._apiConfig.headers.set('Content-Type', 'application/json; charset=utf-8');
    }
    return this;
  }

  _callApi<T>(endpointPath: string, options: RequestInit): Promise<T> {
    const requestPath = this._getRequestPath(endpointPath);
    return fetch(requestPath, options).then((response) => {
      if (!response.ok) {
        return response.json().then((message) => {
          const err = new ApiResponseError(message, response);
          return Promise.reject(err);
        });
      }

      if (response.status === HttpStatus.NO_CONTENT) {
        return '';
      }

      return response.json();
    });
  }

  /**
   * If the V2 API Token is present, use the regular request path.
   * If the V2 API Token is missing, but the page's URL query string params
   * include the V1 API Key, append the V1 API Key to the request path as a
   * query string param.
   */
  _getRequestPath(endpointPath: string) {
    // Makes sure the endpoint path the user gives doesn't start with a /
    const path = endpointPath.startsWith('/') ? endpointPath.substring(1) : endpointPath;

    let requestPath = this._apiUrl + path;

    if (!this.allowApiKeyAuthFallback) {
      return requestPath;
    }

    /**
     * If we allow API key auth fallback, a V2 API Token (OAuth Token) takes precedence, so we use
     * that if one is present. If not, then try using an API Key if one is present.
     * If we don't allow API key auth fallback, we don't need to do anything
     */

    const urlParams = new URLSearchParams(window.location.search);
    const urlHasApiKey = urlParams.has('api_key');
    if (!urlHasApiKey) {
      // If there's no API key, then we can't do anything
      return requestPath;
    }

    const apiKey = urlParams.get('api_key') || '';
    const url = new URL(requestPath);
    url.searchParams.set('api_key', apiKey);
    requestPath = url.toString();

    return requestPath;
  }

  _callApiWithMethod(
    method: RequestInit['method'],
    endpointPath: string,
    options: RequestInit = {},
  ): Promise<unknown> {
    // if both the base config and the options have headers, combine them
    const baseHeaders = new Headers(this._apiConfig.headers);
    const optionHeaders = new Headers(options.headers);
    const headers = new Headers(baseHeaders);
    optionHeaders.forEach((value, key) => {
      headers.set(key, value);
    });

    return this._callApi(endpointPath, { ...this._apiConfig, ...options, headers, method });
  }
}
